import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Switch, Space, Input, Statistic, Tag, Table, message, Row, Col, Card, Progress, Button, Divider, Select, Flex, Empty } from 'antd';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { fetchAuthSession } from "aws-amplify/auth";
import {
  ExportOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  SoundOutlined,
  FileOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import './styles.css';
import { Label } from '@aws-amplify/ui-react';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'tiff', 'ico'];

const getFileExtension = (filename) => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const isImageFile = (filename) => {
  return IMAGE_EXTENSIONS.includes(getFileExtension(filename));
};

const getFileIcon = (filename) => {
  const ext = getFileExtension(filename);
  const iconStyle = { fontSize: 16, marginRight: 6 };
  const previewIconStyle = { fontSize: 48 };

  const iconMap = {
    pdf: { icon: <FilePdfOutlined style={iconStyle} />, preview: <FilePdfOutlined style={{ ...previewIconStyle, color: '#e5252a' }} /> },
    doc: { icon: <FileWordOutlined style={iconStyle} />, preview: <FileWordOutlined style={{ ...previewIconStyle, color: '#2b579a' }} /> },
    docx: { icon: <FileWordOutlined style={iconStyle} />, preview: <FileWordOutlined style={{ ...previewIconStyle, color: '#2b579a' }} /> },
    xls: { icon: <FileExcelOutlined style={iconStyle} />, preview: <FileExcelOutlined style={{ ...previewIconStyle, color: '#217346' }} /> },
    xlsx: { icon: <FileExcelOutlined style={iconStyle} />, preview: <FileExcelOutlined style={{ ...previewIconStyle, color: '#217346' }} /> },
    csv: { icon: <FileExcelOutlined style={iconStyle} />, preview: <FileExcelOutlined style={{ ...previewIconStyle, color: '#217346' }} /> },
    ppt: { icon: <FilePptOutlined style={iconStyle} />, preview: <FilePptOutlined style={{ ...previewIconStyle, color: '#d24726' }} /> },
    pptx: { icon: <FilePptOutlined style={iconStyle} />, preview: <FilePptOutlined style={{ ...previewIconStyle, color: '#d24726' }} /> },
    zip: { icon: <FileZipOutlined style={iconStyle} />, preview: <FileZipOutlined style={previewIconStyle} /> },
    rar: { icon: <FileZipOutlined style={iconStyle} />, preview: <FileZipOutlined style={previewIconStyle} /> },
    '7z': { icon: <FileZipOutlined style={iconStyle} />, preview: <FileZipOutlined style={previewIconStyle} /> },
    tar: { icon: <FileZipOutlined style={iconStyle} />, preview: <FileZipOutlined style={previewIconStyle} /> },
    gz: { icon: <FileZipOutlined style={iconStyle} />, preview: <FileZipOutlined style={previewIconStyle} /> },
    txt: { icon: <FileTextOutlined style={iconStyle} />, preview: <FileTextOutlined style={previewIconStyle} /> },
    log: { icon: <FileTextOutlined style={iconStyle} />, preview: <FileTextOutlined style={previewIconStyle} /> },
    mp4: { icon: <PlayCircleOutlined style={iconStyle} />, preview: <PlayCircleOutlined style={previewIconStyle} /> },
    avi: { icon: <PlayCircleOutlined style={iconStyle} />, preview: <PlayCircleOutlined style={previewIconStyle} /> },
    mov: { icon: <PlayCircleOutlined style={iconStyle} />, preview: <PlayCircleOutlined style={previewIconStyle} /> },
    wmv: { icon: <PlayCircleOutlined style={iconStyle} />, preview: <PlayCircleOutlined style={previewIconStyle} /> },
    mp3: { icon: <SoundOutlined style={iconStyle} />, preview: <SoundOutlined style={previewIconStyle} /> },
    wav: { icon: <SoundOutlined style={iconStyle} />, preview: <SoundOutlined style={previewIconStyle} /> },
    ogg: { icon: <SoundOutlined style={iconStyle} />, preview: <SoundOutlined style={previewIconStyle} /> },
  };

  // Image files get image icon
  if (IMAGE_EXTENSIONS.includes(ext)) {
    return {
      icon: <FileImageOutlined style={iconStyle} />,
      preview: <FileImageOutlined style={{ ...previewIconStyle, color: '#4573D2' }} />,
    };
  }

  return iconMap[ext] || {
    icon: <FileOutlined style={iconStyle} />,
    preview: <FileOutlined style={previewIconStyle} />,
  };
};

const handleSingleDownload = async (downloadUrl, filename) => {
  try {
    const { data } = await axios.get(downloadUrl, { responseType: 'blob' });
    saveAs(data, filename);
  } catch (error) {
    message.error('Failed to download file.');
  }
};

const twoColors = { '0%': '#108ee9', '100%': '#87d068' };
const Downloader = () => {

  const [attachments, setAttachments] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [downloadProgressMap, setDownloadProgressMap] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [filterTable, setFilterTable] = useState(null);
  const [listOfProjects, setListOfProjects] = useState(null);
  const [listOfTasks, setListOfTasks] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [customCode, setCustomCode] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [taskName, setTaskName] = useState(null);
  const [taskLink, setTaskLink] = useState(null);
  const [preview, setPreview] = useState(() => localStorage.getItem('imagePreview') === 'true');
  const [includeSubtasks, setIncludeSubtasks] = useState(false);
  const [subtaskLoading, setSubtaskLoading] = useState(false);

  useEffect(() => {
    const fetchAttachments = async () => {
      try {
        let taskID = new URLSearchParams(window.location.search).get('task');
        let projectID = new URLSearchParams(window.location.search).get('project');
        if (!taskID) {
          taskID = sessionStorage.getItem('taskID');
        }
        let workspaceID = new URLSearchParams(window.location.search).get('workspace');
        if (!workspaceID) {
          workspaceID = sessionStorage.getItem('workspaceID');
        }

        const uSession = await fetchAuthSession();
        const idToken = uSession.tokens.idToken.payload;
        const customCode = idToken['custom:code'];
        setCustomCode(customCode);


        if (workspaceID) {
          const listOfProjects = await axios.get('https://app.asana.com/api/1.0/workspaces/' + workspaceID + '/projects', {
            headers: {
              'Authorization': `Bearer ${customCode}`,
              'accept': 'application/json'
            }
          });
          setListOfProjects(listOfProjects);
        }

        if (projectID) {
          console.log("project id is", projectID);

          const response = await axios.get('https://app.asana.com/api/1.0/attachments', {
            headers: {
              'Authorization': `Bearer ${customCode}`,
              'accept': 'application/json'
            },
            params: {
              opt_fields: 'gid,download_url,name,size,created_at,view_url,resource_subtype',
              parent: projectID
            }
          });
          console.log(response);

          const filteredAttachments = response.data.data.filter(attachment => attachment.resource_subtype === 'asana');
          const attachmentsWithKeys = filteredAttachments.map((attachment, index) => ({ ...attachment, key: index.toString() }));
          setAttachments(attachmentsWithKeys);

          const totalBytes = filteredAttachments.reduce((total, attachment) => total + attachment.size, 0);
          setTotalSize(totalBytes);
        }

        if (taskID) {
          const taskName = await axios.get('https://app.asana.com/api/1.0/tasks/' + taskID + '?opt_fields=name,permalink_url', {
            headers: {
              'Authorization': `Bearer ${customCode}`,
              'accept': 'application/json'
            }
          });
          setTaskName(taskName.data.data.name);
          setTaskLink(taskName.data.data.permalink_url);

          const response = await axios.get('https://app.asana.com/api/1.0/attachments', {
            headers: {
              'Authorization': `Bearer ${customCode}`,
              'accept': 'application/json'
            },
            params: {
              opt_fields: 'gid,download_url,name,size,created_at,view_url,resource_subtype',
              parent: taskID
            }
          });

          const filteredAttachments = response.data.data.filter(attachment => attachment.resource_subtype === 'asana');
          const attachmentsWithKeys = filteredAttachments.map((attachment, index) => ({ ...attachment, key: index.toString() }));
          setAttachments(attachmentsWithKeys);

          const totalBytes = filteredAttachments.reduce((total, attachment) => total + attachment.size, 0);
          setTotalSize(totalBytes);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchAttachments();
  }, []);


  useEffect(() => {
    setSelectedTaskId(null);
  }, [selectedProject]);

  var parseListOfTasks = async (selectedProject) => {

    const listOfTasks = await axios.get('https://app.asana.com/api/1.0/projects/' + selectedProject + '/tasks', {
      headers: {
        'Authorization': `Bearer ${customCode}`,
        'accept': 'application/json'
      }
    })

    setListOfTasks(listOfTasks.data.data);
  };

  const updateDownloadProgress = (key, percentCompleted) => {
    setDownloadProgressMap(prevState => ({
      ...prevState,
      [key]: percentCompleted
    }));
  };

  const calculateOverallProgress = () => {
    const numAttachments = attachments.length;
    if (numAttachments === 0) return 0;

    const totalProgress = Object.values(downloadProgressMap).reduce((acc, curr) => acc + curr, 0);
    return Math.round(totalProgress / numAttachments);
  };

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    const zip = new JSZip();


    await Promise.all(attachments.map(async attachment => {
      try {
        const { data } = await axios.get(attachment.download_url, {
          responseType: 'blob',
          onDownloadProgress: progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            updateDownloadProgress(attachment.key, percentCompleted);
          }
        });
        zip.file(attachment.name, data);
      } catch (error) {
        console.error('Error downloading attachment:', error);
      }
    }));

    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, 'all_attachments.zip');
    setIsDownloading(false);
    setDownloadProgressMap({});
  };

  const handleDownloadSelected = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one file.');
      return;
    }

    setIsDownloading(true);
    const zip = new JSZip();

    await Promise.all(selectedRowKeys.map(async rowKey => {
      const attachment = attachments.find(a => a.key === rowKey);
      if (attachment) {
        try {
          const { data } = await axios.get(attachment.download_url, {
            responseType: 'blob',
            onDownloadProgress: progressEvent => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              updateDownloadProgress(attachment.key, percentCompleted);
            }
          });
          zip.file(attachment.name, data);
        } catch (error) {
          console.error('Error downloading attachment:', error);
        }
      }
    }));

    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, 'selected_attachments.zip');
    setIsDownloading(false);
    setDownloadProgressMap({});
  }, [selectedRowKeys, attachments]);

  // Keyboard shortcuts: Ctrl/Cmd+A to select all, Ctrl/Cmd+D to download selected
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      // Don't trigger when typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (isCtrlOrCmd && e.key === 'a') {
        e.preventDefault();
        setSelectedRowKeys(attachments.map(a => a.key));
      }
      if (isCtrlOrCmd && e.key === 'd') {
        e.preventDefault();
        handleDownloadSelected();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [attachments, handleDownloadSelected]);

  const goToNewURL = () => {
    let workspaceID = new URLSearchParams(window.location.search).get('workspace');
    if (!workspaceID) {
      workspaceID = sessionStorage.getItem('workspaceID');
    }
    window.location.href = `/downloader?workspace=${workspaceID}&task=${selectedTaskId}`;
  }
  const handleProjectChange = (info) => {
    console.log('Selected project:', info);
    setSelectedProject(info);
    parseListOfTasks(info);
  };
  const handleTaskChange = (taskId) => {
    console.log("task changed to", taskId);
    setSelectedTaskId(taskId);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const hasSelected = selectedRowKeys.length > 0;

  const switchPreview = (checked) => {
    setPreview(checked);
    localStorage.setItem('imagePreview', checked);
  };

  const fetchSubtaskAttachments = async () => {
    let taskID = new URLSearchParams(window.location.search).get('task');
    if (!taskID) taskID = sessionStorage.getItem('taskID');
    if (!taskID || !customCode) return;

    setSubtaskLoading(true);
    try {
      const subtasksResponse = await axios.get(
        `https://app.asana.com/api/1.0/tasks/${taskID}/subtasks`,
        {
          headers: { 'Authorization': `Bearer ${customCode}`, 'accept': 'application/json' },
          params: { opt_fields: 'gid,name' },
        }
      );

      const subtasks = subtasksResponse.data.data;
      if (subtasks.length === 0) {
        message.info('No subtasks found for this task.');
        setSubtaskLoading(false);
        setIncludeSubtasks(false);
        return;
      }

      const subtaskAttachments = [];
      await Promise.all(
        subtasks.map(async (subtask) => {
          try {
            const response = await axios.get('https://app.asana.com/api/1.0/attachments', {
              headers: { 'Authorization': `Bearer ${customCode}`, 'accept': 'application/json' },
              params: {
                opt_fields: 'gid,download_url,name,size,created_at,view_url,resource_subtype',
                parent: subtask.gid,
              },
            });
            const filtered = response.data.data.filter((a) => a.resource_subtype === 'asana');
            filtered.forEach((a) => {
              subtaskAttachments.push({ ...a, isSubtask: true, subtaskName: subtask.name });
            });
          } catch (error) {
            console.error(`Error fetching attachments for subtask ${subtask.name}:`, error);
          }
        })
      );

      if (subtaskAttachments.length === 0) {
        message.info('Subtasks have no attachments.');
        setSubtaskLoading(false);
        setIncludeSubtasks(false);
        return;
      }

      const keyedSubtaskAttachments = subtaskAttachments.map((a, index) => ({
        ...a,
        key: `sub-${index}`,
      }));

      const allAttachments = [...attachments.filter((a) => !a.isSubtask), ...keyedSubtaskAttachments];
      setAttachments(allAttachments);
      setTotalSize(allAttachments.reduce((total, a) => total + a.size, 0));
      message.success(`Loaded ${subtaskAttachments.length} file(s) from ${subtasks.length} subtask(s).`);
    } catch (error) {
      message.error('Failed to fetch subtask attachments.');
      setIncludeSubtasks(false);
    } finally {
      setSubtaskLoading(false);
    }
  };

  const removeSubtaskAttachments = () => {
    const mainAttachments = attachments.filter((a) => !a.isSubtask);
    setAttachments(mainAttachments);
    setTotalSize(mainAttachments.reduce((total, a) => total + a.size, 0));
    setSelectedRowKeys((prev) => prev.filter((key) => !key.startsWith('sub-')));
    setFilterTable(null);
  };

  const switchSubtasks = (checked) => {
    setIncludeSubtasks(checked);
    if (checked) {
      fetchSubtaskAttachments();
    } else {
      removeSubtaskAttachments();
    }
  };

  const columns = [
    ...(preview
      ? [
        {
          title: 'Preview',
          dataIndex: 'view_url',
          key: 'preview',
          width: 180,
          render: (viewUrl, record) => {
            if (isImageFile(record.name) && viewUrl) {
              return (
                <div style={{ width: 160, height: 130, overflow: 'hidden', borderRadius: 6 }}>
                  <img src={viewUrl} alt="Preview" crossOrigin="anonymous" className="preview-image" />
                </div>
              );
            }
            // Non-image: show file type icon
            return (
              <div style={{ width: 160, height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, backgroundColor: '#f6f8f9' }}>
                {getFileIcon(record.name).preview}
              </div>
            );
          },
        },
      ]
      : []),
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          {getFileIcon(name).icon}
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => handleSingleDownload(record.download_url, name)}
          >
            {name}
          </Button>
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Size (MB)',
      dataIndex: 'size',
      key: 'size',
      width: 115,
      render: size => (size / (1024 * 1024)).toFixed(2),
      sorter: (a, b) => a.size - b.size,
    },
    {
      title: 'Created at',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 200,
      render: (created_at) => {
        const formattedTime = new Date(created_at).toLocaleString();
        return formattedTime;
      },
      sorter: (a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      },
    },
    {
      title: 'Download',
      dataIndex: 'download_url',
      key: 'download',
      width: 120,
      render: (downloadUrl, record) => (
        <Button
          type="text"
          icon={<DownloadOutlined />}
          onClick={() => handleSingleDownload(downloadUrl, record.name)}
        />
      ),
    },
  ];

  const search = value => {
    const filterTable = attachments.filter(attachment =>
      attachment.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilterTable(filterTable);
  };


  return (
    <>
      <Row justify={"center"}>
        <Col span={23}>
          <Card
            title={
              taskName && taskLink ? (
                <>
                  {"Download All or Selected Files from "}
                  <a href={taskLink} target="_blank" rel="noopener noreferrer">{taskName}</a>
                </>
              ) : "Download All or Selected Files"
            }
            style={{ margin: '16px 0', border: 'none', boxShadow: 'none' }}
            bordered={false}
            extra={<Space>
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleProjectChange}
                style={{ width: '100%', minWidth: 170 }}
                placeholder="Quick navigation"
              >
                {listOfProjects ? (
                  listOfProjects.data.data.map((project) => (
                    <Select.Option key={project.gid} value={project.gid}>
                      {project.name}
                    </Select.Option>
                  ))
                ) : (
                  ""
                )}
              </Select>

              {selectedProject && listOfTasks ? (
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  value={selectedTaskId}
                  onChange={handleTaskChange}
                  style={{ width: '100%', minWidth: 170 }}
                  placeholder="Select Task"
                >
                  {listOfTasks.map((task) => (
                    <Select.Option key={task.gid} value={task.gid}>
                      {task.name}
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                ""
              )}
              {selectedTaskId ?
                <Button type="primary" onClick={goToNewURL}><ExportOutlined /></Button>
                : null}

            </Space>
            }>
            {isDownloading ? (
              <>
                <Progress percent={calculateOverallProgress()} strokeColor={twoColors} status="active" />
                <Tag>To cancel the download close this window</Tag>
              </>
            ) : (
              <Row justify={"space-evenly"}>
                <Col span={10}>
                  <Card>
                    <Row justify={"space-evenly"}>
                      <Col>
                        <Button type="primary" disabled={attachments.length <= 0} onClick={handleDownloadAll}>Download All</Button>
                      </Col>
                      <Col>
                        <Button type="primary" disabled={!hasSelected} onClick={handleDownloadSelected}>Download Selected</Button>
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="Selected files size"
                      value={(selectedRowKeys.reduce((total, key) => {
                        const attachment = attachments.find(a => a.key === key);
                        return total + (attachment ? attachment.size : 0);
                      }, 0) / (1024 * 1024)).toFixed(2)}
                      suffix="MB" />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic title="Total Size" value={(totalSize / (1024 * 1024)).toFixed(2)} suffix="MB" />
                  </Card>
                </Col>

              </Row>
            )}
            <Divider></Divider>
            <Flex justify='space-between'>
              <Space size="large">
                <Space>
                  <Label>Image Preview</Label>
                  <Switch value={preview} onChange={switchPreview} />
                </Space>
                {taskName && (
                  <Space>
                    <Label>Include sub-tasks</Label>
                    <Switch value={includeSubtasks} onChange={switchSubtasks} loading={subtaskLoading} />
                  </Space>
                )}
              </Space>
              <Space>
                <Input.Search
                  placeholder="Search by name"
                  allowClear
                  onSearch={search} />
                <Button onClick={() => setFilterTable(attachments)}>Reset</Button>
              </Space>
            </Flex>
            <Divider></Divider>
            {!loading && attachments.length === 0 ? (
              <Empty
                description="No attachments found for this task"
                style={{ padding: '40px 0' }}
              />
            ) : (
              <Table loading={loading || subtaskLoading} sticky pagination={false} dataSource={filterTable || attachments} columns={columns} rowSelection={rowSelection} rowClassName={(record) => record.isSubtask ? 'subtask-row' : ''} />
            )}
          </Card>
        </Col>
      </Row >
    </>
  );
};

export default Downloader;
